// App.js
import * as React from "react";
import * as ReactDOM from "react-dom/client";
import Dashboard from "./Dashboard";
import PrivateRoute from "./components/PrivateRoute";
import ErrorPage from "./error-page";
import MainGrid from './components/MainGrid';
import {
    createBrowserRouter,
    RouterProvider, useNavigate, Navigate, useLocation,
} from "react-router-dom";
import "./index.css";
import "./css/Root.css"
import Root from "./routes/root";
import SignIn from "./components/signin/SignIn"
import SignUp from "./components/signup/SignUp"
import useToken from './components/useToken'
import { AlertProvider } from './components/useAlert';
import Configuration from './components/Configuration';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ActorMainGrid from "./components/ActorConfigMainGrid";
import AlertMainGrid from "./components/AlertConfigMainGrid";
import axios from "axios";
import GrowSetupWelcome from "./components/grow_setup/GrowSetupWelcome";
import AnalyticsGrid from "./components/AnalyticsGrid";
import {jwtDecode} from "jwt-decode";
import Cookies from "js-cookie";
import GrowSetupConfiguration from "./components/GrowSetupConfiguration";
import { GrowSetupProvider } from './GrowSetupContext';
import { AuthProvider } from "./components/signin/AuthContext";
import { ConfigProvider } from './components/ConfigContext';
import DuengePlan from "./components/DuengePlan";
import MainGrid_Wrapper from "./components/MainGridWrapper";

// v3 imports
import V3Layout       from "./v3/Layout";
import V3SignIn       from "./v3/SignIn";
import V3Dashboard    from "./v3/pages/DashboardPage";
import V3Analytics    from "./v3/pages/AnalyticsPage";
import V3Config       from "./v3/pages/ConfigPage";
import V3ActorConfig  from "./v3/pages/ActorConfigPage";
import V3AlertConfig  from "./v3/pages/AlertConfigPage";
import V3GrowPlan     from "./v3/pages/GrowPlanPage";
import V3Settings     from "./v3/pages/SettingsPage";

// v4 imports
import V4Layout       from "./v4/Layout";
import V4SignIn       from "./v4/SignIn";
import V4Dashboard    from "./v4/pages/DashboardPage";
import V4Analytics    from "./v4/pages/AnalyticsPage";
import V4Config       from "./v4/pages/ConfigPage";
import V4ActorConfig  from "./v4/pages/ActorConfigPage";
import V4AlertConfig  from "./v4/pages/AlertConfigPage";
import V4GrowPlan     from "./v4/pages/GrowPlanPage";
import V4Settings     from "./v4/pages/SettingsPage";

// v5–v7: alternative shells/themes on top of the v4 pages
import V5Layout       from "./v5/Layout";
import V6Layout       from "./v6/Layout";
import V7Layout       from "./v7/Layout";
import { V5_VARIANT } from "./v5/theme";
import { V6_VARIANT } from "./v6/theme";
import { V7_VARIANT } from "./v7/theme";

/* Pages shared by the default UI (v4) and the v5–v7 variants */
const PAGE_ROUTES = [
    { path: "dashboard",   element: <V4Dashboard  /> },
    { path: "analytics",   element: <V4Analytics  /> },
    { path: "config",      element: <V4Config     /> },
    { path: "actorconfig", element: <V4ActorConfig/> },
    { path: "alertconfig", element: <V4AlertConfig/> },
    { path: "grow_plan",   element: <V4GrowPlan   /> },
    { path: "settings",    element: <V4Settings   /> },
];

function variantRoutes(base, variant, Layout) {
    return [
        { path: `${base}/signin`, element: <V4SignIn variant={variant} />, errorElement: <ErrorPage /> },
        {
            path: base,
            element: <Layout />,
            children: [{ index: true, element: <Navigate to="dashboard" replace /> }, ...PAGE_ROUTES],
        },
    ];
}

/* Old /v4/... links now live at the root */
function LegacyV4Redirect() {
    const { pathname, search } = useLocation();
    const rest = pathname.replace(/^\/v4/, "");
    return <Navigate to={(rest === "/signin" || rest === "" || rest === "/" ? "/" : rest) + search} replace />;
}

function App(){

    const Callback = () => {
        const called = React.useRef(false)
        const { checkLoginState, loggedIn } = React.useContext()
        const navigate = useNavigate()
        React.useEffect(() => {
            ;(async () => {
                if (loggedIn === false) {
                    try {
                        if (called.current) return // prevent rerender caused by StrictMode
                        called.current = true
                        const res = await axios.get(`/auth/token${window.location.search}`)
                        console.log('response: ', res)
                        checkLoginState()
                        navigate('/')
                    } catch (err) {
                        console.error(err)
                        navigate('/')
                    }
                } else if (loggedIn === true) {
                    navigate('/dashboard')
                }
            })()
        }, [checkLoginState, loggedIn, navigate])
        return <></>
    }

    const router = createBrowserRouter([
        // ── default UI (v4) at the root ─────────────────────
        {
            path: "/",
            element: <V4SignIn />,
            errorElement: <ErrorPage />,
        },
        {
            element: <V4Layout />,
            children: PAGE_ROUTES,
        },
        { path: "/v4/*", element: <LegacyV4Redirect /> },
        // ── alternative UIs to try out ──────────────────────
        ...variantRoutes("/v5", V5_VARIANT, V5Layout),
        ...variantRoutes("/v6", V6_VARIANT, V6Layout),
        ...variantRoutes("/v7", V7_VARIANT, V7Layout),
        // ── old UI ─────────────────────────────────────────
        {
            path: "/legacy",
            element: <SignIn  />,
            errorElement: <ErrorPage />,
        },
        {
            path: "/signup",
            element: <SignUp  />,
            errorElement: <ErrorPage />,
        },
        {
            path: '/auth/callback',
            element: <Callback />,
        },
        {
            path: 'new_grow_setup',
            element: <GrowSetupWelcome />,
        },
        // ── v3 routes ──────────────────────────────────────
        {
            path: "/v3/signin",
            element: <V3SignIn />,
            errorElement: <ErrorPage />,
        },
        {
            path: "/v3",
            element: <V3Layout />,
            children: [
                { path: "dashboard",   element: <V3Dashboard  /> },
                { path: "analytics",   element: <V3Analytics  /> },
                { path: "config",      element: <V3Config     /> },
                { path: "actorconfig", element: <V3ActorConfig/> },
                { path: "alertconfig", element: <V3AlertConfig/> },
                { path: "grow_plan",   element: <V3GrowPlan   /> },
                { path: "settings",    element: <V3Settings   /> },
            ],
        },
        // ── existing routes (unchanged) ─────────────────
        {
            path: "/grow_setup",
            element: <PrivateRoute><Dashboard  /></PrivateRoute>,
            children: [
                {
                    path: "analytics",
                    element: <AnalyticsGrid />,
                },
                {
                    path: "dashboard",
                    element: <MainGrid_Wrapper />,
                },
                {
                    path: "config",
                    element: <Configuration />,
                },
                {
                    path: "actorconfig",
                    element: <ActorMainGrid  />,
                },
                {
                    path: "alertconfig",
                    element: <AlertMainGrid />,
                },
                {
                    path: "settings",
                    element: <GrowSetupConfiguration  />,
                },
                {
                    path: "grow_plan",
                    element: <DuengePlan/>,
                },
                {
                    path: 'new_grow_setup',
                    element: <GrowSetupWelcome />,
                }
            ],
        },
    ]);

    return(
        <React.StrictMode>
            <AlertProvider>
                <GoogleOAuthProvider clientId="353910049156-q7b09ssfncll67d0piktcktbd32o8guf.apps.googleusercontent.com">
                    <AuthProvider>
                        <GrowSetupProvider>
                            <ConfigProvider>
                                <RouterProvider router={router} />
                            </ConfigProvider>
                        </GrowSetupProvider>
                    </AuthProvider>
                </GoogleOAuthProvider>
            </AlertProvider>
        </React.StrictMode>
    );
}
export default App;